import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { MapPin, Mail, Phone, Clock, Send, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { format, addDays, isWeekend } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('form');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  // Appointment state
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentData, setAppointmentData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    notes: ''
  });
  const [appointmentLoading, setAppointmentLoading] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await axios.get(`${API}/appointments/available/${date}`);
      setAvailableSlots(response.data.available_slots);
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      toast.success(t.contact.form.success);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t.contact.form.error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) return;
    
    setAppointmentLoading(true);
    
    try {
      await axios.post(`${API}/appointments`, {
        ...appointmentData,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot,
        language
      });
      toast.success(t.contact.appointment.success);
      setSelectedDate(null);
      setSelectedSlot(null);
      setAppointmentData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error(error.response?.data?.detail || t.contact.form.error);
    } finally {
      setAppointmentLoading(false);
    }
  };

  const disabledDays = (date) => {
    return isWeekend(date) || date < new Date();
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="font-heading text-sm font-semibold tracking-[0.2em] text-[#E5A81D] mb-4 block animate-fade-in-up">
            {t.contact.subtitle}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6 animate-fade-in-up animation-delay-100">
            {t.contact.title}
          </h1>
          <p className="font-body text-lg text-slate-600 leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.contact.description}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10">
                    <MapPin size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-[#0F172A] mb-2">
                      {language === 'fr' ? 'Adresse' : 'Address'}
                    </h3>
                    <p className="font-body text-slate-600 text-sm">
                      {t.contact.info.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10">
                    <Mail size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-[#0F172A] mb-2">
                      Email
                    </h3>
                    <a href="mailto:contact@walaby.fr" className="font-body text-slate-600 text-sm hover:text-[#E5A81D] transition-colors">
                      {t.contact.info.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10">
                    <Phone size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-[#0F172A] mb-2">
                      {language === 'fr' ? 'Téléphone' : 'Phone'}
                    </h3>
                    <p className="font-body text-slate-600 text-sm">
                      {t.contact.info.phone}
                    </p>
                    <p className="font-body text-slate-600 text-sm">
                      {t.contact.info.mobile}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#E5A81D]/10">
                    <Clock size={24} className="text-[#E5A81D]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-[#0F172A] mb-2">
                      {language === 'fr' ? 'Horaires' : 'Hours'}
                    </h3>
                    <p className="font-body text-slate-600 text-sm">
                      {language === 'fr' ? 'Lun - Ven: 9h - 18h' : 'Mon - Fri: 9am - 6pm'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              {/* Tab Navigation */}
              <div className="flex space-x-4 mb-8 border-b border-slate-200">
                <button
                  onClick={() => setActiveTab('form')}
                  data-testid="tab-form"
                  className={`flex items-center space-x-2 px-4 py-3 font-heading text-sm font-medium transition-all ${
                    activeTab === 'form'
                      ? 'text-[#0F172A] border-b-2 border-[#E5A81D]'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Send size={18} />
                  <span>{language === 'fr' ? 'Message' : 'Message'}</span>
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  data-testid="tab-appointment"
                  className={`flex items-center space-x-2 px-4 py-3 font-heading text-sm font-medium transition-all ${
                    activeTab === 'appointment'
                      ? 'text-[#0F172A] border-b-2 border-[#E5A81D]'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <CalendarDays size={18} />
                  <span>{t.contact.appointment.title}</span>
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === 'form' && (
                <form onSubmit={handleFormSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="font-heading text-sm font-medium text-slate-700">
                        {t.contact.form.name} *
                      </Label>
                      <Input
                        id="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-2 input-premium"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-heading text-sm font-medium text-slate-700">
                        {t.contact.form.email} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-2 input-premium"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="font-heading text-sm font-medium text-slate-700">
                        {t.contact.form.company}
                      </Label>
                      <Input
                        id="company"
                        data-testid="input-company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="mt-2 input-premium"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-heading text-sm font-medium text-slate-700">
                        {t.contact.form.phone}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2 input-premium"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="font-heading text-sm font-medium text-slate-700">
                      {t.contact.form.subject} *
                    </Label>
                    <Input
                      id="subject"
                      data-testid="input-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="mt-2 input-premium"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-heading text-sm font-medium text-slate-700">
                      {t.contact.form.message} *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="mt-2 input-premium resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={formLoading}
                    data-testid="submit-contact"
                    className="w-full md:w-auto bg-[#0F172A] hover:bg-[#1E293B] text-white px-8 py-4 font-heading text-sm font-medium"
                  >
                    {formLoading ? '...' : t.contact.form.submit}
                  </Button>
                </form>
              )}

              {/* Appointment Form */}
              {activeTab === 'appointment' && (
                <div className="space-y-8" data-testid="appointment-form">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Calendar */}
                    <div>
                      <Label className="font-heading text-sm font-medium text-slate-700 mb-4 block">
                        {t.contact.appointment.selectDate}
                      </Label>
                      <div className="border border-slate-200 p-4 inline-block">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={disabledDays}
                          locale={language === 'fr' ? fr : enUS}
                          fromDate={new Date()}
                          toDate={addDays(new Date(), 60)}
                          className="font-body"
                          data-testid="calendar"
                        />
                      </div>
                    </div>

                    {/* Time Slots */}
                    <div>
                      <Label className="font-heading text-sm font-medium text-slate-700 mb-4 block">
                        {t.contact.appointment.selectTime}
                      </Label>
                      {selectedDate ? (
                        availableSlots.length > 0 ? (
                          <div className="grid grid-cols-2 gap-3">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot}
                                onClick={() => setSelectedSlot(slot)}
                                data-testid={`slot-${slot}`}
                                className={`p-3 border text-sm font-heading font-medium transition-all ${
                                  selectedSlot === slot
                                    ? 'border-[#E5A81D] bg-[#E5A81D]/10 text-[#0F172A]'
                                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                                }`}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 font-body">{t.contact.appointment.noSlots}</p>
                        )
                      ) : (
                        <p className="text-slate-500 font-body">
                          {language === 'fr' ? 'Sélectionnez une date' : 'Select a date first'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Appointment Details Form */}
                  {selectedDate && selectedSlot && (
                    <form onSubmit={handleAppointmentSubmit} className="space-y-6 border-t border-slate-200 pt-8">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="apt-name" className="font-heading text-sm font-medium text-slate-700">
                            {t.contact.form.name} *
                          </Label>
                          <Input
                            id="apt-name"
                            data-testid="apt-input-name"
                            value={appointmentData.name}
                            onChange={(e) => setAppointmentData({ ...appointmentData, name: e.target.value })}
                            required
                            className="mt-2 input-premium"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apt-email" className="font-heading text-sm font-medium text-slate-700">
                            {t.contact.form.email} *
                          </Label>
                          <Input
                            id="apt-email"
                            type="email"
                            data-testid="apt-input-email"
                            value={appointmentData.email}
                            onChange={(e) => setAppointmentData({ ...appointmentData, email: e.target.value })}
                            required
                            className="mt-2 input-premium"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="apt-company" className="font-heading text-sm font-medium text-slate-700">
                            {t.contact.form.company}
                          </Label>
                          <Input
                            id="apt-company"
                            data-testid="apt-input-company"
                            value={appointmentData.company}
                            onChange={(e) => setAppointmentData({ ...appointmentData, company: e.target.value })}
                            className="mt-2 input-premium"
                          />
                        </div>
                        <div>
                          <Label htmlFor="apt-phone" className="font-heading text-sm font-medium text-slate-700">
                            {t.contact.form.phone}
                          </Label>
                          <Input
                            id="apt-phone"
                            type="tel"
                            data-testid="apt-input-phone"
                            value={appointmentData.phone}
                            onChange={(e) => setAppointmentData({ ...appointmentData, phone: e.target.value })}
                            className="mt-2 input-premium"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="apt-subject" className="font-heading text-sm font-medium text-slate-700">
                          {t.contact.form.subject} *
                        </Label>
                        <Input
                          id="apt-subject"
                          data-testid="apt-input-subject"
                          value={appointmentData.subject}
                          onChange={(e) => setAppointmentData({ ...appointmentData, subject: e.target.value })}
                          required
                          className="mt-2 input-premium"
                        />
                      </div>

                      <div>
                        <Label htmlFor="apt-notes" className="font-heading text-sm font-medium text-slate-700">
                          Notes
                        </Label>
                        <Textarea
                          id="apt-notes"
                          data-testid="apt-input-notes"
                          value={appointmentData.notes}
                          onChange={(e) => setAppointmentData({ ...appointmentData, notes: e.target.value })}
                          rows={3}
                          className="mt-2 input-premium resize-none"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="font-body text-sm text-slate-600">
                          <span className="font-medium text-[#0F172A]">
                            {format(selectedDate, 'PPPP', { locale: language === 'fr' ? fr : enUS })}
                          </span>
                          {' '}{language === 'fr' ? 'à' : 'at'}{' '}
                          <span className="font-medium text-[#E5A81D]">{selectedSlot}</span>
                        </div>
                        <Button
                          type="submit"
                          disabled={appointmentLoading}
                          data-testid="submit-appointment"
                          className="bg-[#E5A81D] hover:bg-[#E5A81D]/90 text-[#0F172A] px-8 py-4 font-heading text-sm font-bold"
                        >
                          {appointmentLoading ? '...' : t.contact.appointment.book}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
